import type { ReactNode } from 'react';

import {
  MetricsBox,
  Panel,
  Box,
  Typography,
  tokens,
} from '@cast/design-system';

import type { MetricsGridItem } from './_types';

type MetricsGridProps = {
  rows: MetricsGridItem[][];
  testId?: string;
  columnsPerRow?: number;
  title?: string;
  adornment?: ReactNode;
};

const MetricsRow = ({
  items,
  columnsPerRow,
}: {
  items: MetricsGridItem[];
  columnsPerRow: number;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columnsPerRow}, 1fr)`,
      px: 12,
      py: 8,
    }}
  >
    {items.map((item, index) => (
      <Box
        key={String(item.label)}
        sx={{
          minWidth: 0,
          ...(index > 0 && {
            pl: 16,
            borderLeft: '1px solid',
            borderColor: tokens.theme.border.structural.neutral,
          }),
        }}
      >
        <MetricsBox
          size="xs"
          slots={{
            label: item.label,
            value: item.value,
            unit: item.unit,
          }}
          slotProps={{
            label: {
              variant: 'L10M',
              color: tokens.theme.fg.structural.secondary,
            },
            value: {
              variant: 'P12M',
              color: tokens.theme.fg.structural.primary,
            },
          }}
        />
      </Box>
    ))}
  </Box>
);

export const MetricsGrid = ({
  rows,
  testId,
  columnsPerRow = 4,
  title,
  adornment,
}: MetricsGridProps) => {
  return (
    <Panel
      testId={testId}
      sx={adornment ? { position: 'relative' } : undefined}
    >
      {title && (
        <Typography
          variant="P12B"
          sx={{ px: 12, pt: 8, color: tokens.theme.fg.structural.primary }}
        >
          {title}
        </Typography>
      )}
      {rows.map((rowItems, index) => (
        <MetricsRow
          key={index}
          items={rowItems}
          columnsPerRow={columnsPerRow}
        />
      ))}
      {adornment && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
          }}
        >
          {adornment}
        </Box>
      )}
    </Panel>
  );
};
